import {
  createQuery,
  keepPreviousData,
  useQueryClient,
} from "@tanstack/solid-query";
import { createFileRoute, useNavigate } from "@tanstack/solid-router";
import AccountTable from "~/components/account/table/account-table";
import { IconEllipsisVertical, IconPlus } from "~/components/icons";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import {
  type AccountsParams,
  AccountsAllResource,
  AccountsResource,
} from "~/features/account/account.resource";
import { getAccountsPageData } from "~/features/account/accounts-page";
import {
  businessDetailResource,
  businessesResource,
  useActiveBusinessId,
} from "~/features/business/business.resource";
import { withBusinessGuard } from "~/lib/route-guards";

export const Route = createFileRoute("/_dashboard/dashboard/accounts/")({
  ...withBusinessGuard(),
  loader: async ({ context }) => {
    const collection = await context.queryClient.ensureQueryData(
      businessesResource.options(undefined),
    );
    const businessId = collection.activeBusinessId!;
    const defaultParams: AccountsParams = { businessId, limit: 20 };

    // One server-side function call — aggregate fetches everything in parallel.
    // During SSR this is a direct function call (no HTTP). On client navigation
    // it's one HTTP round trip instead of three.
    const pageData = await getAccountsPageData({ data: { businessId } });

    // Seed the query cache so child components' createQuery hooks find data
    // without firing individual network requests. During SSR the aggregate is
    // a direct function call; on client navigation it's one HTTP round trip.
    businessDetailResource.seed(
      context.queryClient,
      businessId,
      pageData.businessDetail,
    );
    AccountsResource.seedIfEmpty(
      context.queryClient,
      defaultParams,
      pageData.accountsList,
    );
    AccountsAllResource.seedIfEmpty(
      context.queryClient,
      { businessId },
      pageData.allAccounts,
    );

    return {
      businessId,
      accounts: pageData.accountsList,
      collection,
      activeBusiness: pageData.businessDetail,
      allAccounts: pageData.allAccounts,
    };
  },
  component: AccountsPage,
});

function AccountsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const data = Route.useLoaderData();

  // Surface loader data into the TQ cache synchronously during render, so
  // child components' createQuery hooks never fire a network request on the
  // initial hydration pass. The loader seeded the cache on the server too,
  // but without a dehydration boundary the client starts fresh.
  const d = data();
  if (d) {
    businessDetailResource.seedIfEmpty(qc, d.businessId, d.activeBusiness);
    AccountsAllResource.seedIfEmpty(
      qc,
      { businessId: d.businessId },
      d.allAccounts,
    );
    AccountsResource.seedIfEmpty(
      qc,
      { businessId: d.businessId, limit: 20 } as AccountsParams,
      d.accounts,
    );
  }

  const businessId = useActiveBusinessId();

  const businessQuery = createQuery(() => ({
    ...businessDetailResource.options(businessId() ?? "", !!businessId()),
    placeholderData: keepPreviousData,
  }));

  const classes = () => businessQuery.data?.classes ?? [];

  return (
    <div class="flex flex-1 flex-col gap-6 px-4 sm:px-6 pb-3 pt-6">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-3xl font-bold">Accounts</h1>
          <p class="text-muted-foreground">Manage your accounts.</p>
        </div>
        <DropdownMenu placement="bottom-end">
          <DropdownMenuTrigger
            as={Button<"button">}
            variant="outline"
            size="icon"
          >
            <IconEllipsisVertical class="size-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent class="w-40">
            <DropdownMenuItem
              onClick={() =>
                navigate({
                  to: "/dashboard/accounts/$accountId",
                  params: { accountId: "new" },
                })
              }
            >
              <IconPlus class="size-4" />
              Add Account
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <AccountTable businessId={businessId() ?? ""} classes={classes()} />
    </div>
  );
}
